import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAxiosConfig } from '../utils/http';
import { logout } from './userSlice';

// --- Async Thunks ---

export const fetchProductDetails = createAsyncThunk(
  'productList/fetchProductDetails',
  async (id, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = state?.user?.userInfo?.token;
      const qs = token ? `?adminCheck=${encodeURIComponent(token)}` : '';
      const { data } = await axios.get(`/api/products/${id}${qs}`);
      localStorage.setItem('thisisinside', JSON.stringify(data));
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const listProductDetails = createAsyncThunk(
  'product/details',
  async (id, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = state?.user?.userInfo?.isAdmin ? state?.user?.userInfo?.token : '';
      const qs = token ? `?adminCheck=${encodeURIComponent(token)}` : '';
      const { data } = await axios.get(`/api/products/${id}${qs}`);
      localStorage.setItem('thisisinside', JSON.stringify(data));
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message
      );
    }
  }
);

export const listProductDetailsBySlug = createAsyncThunk(
  'product/detailsBySlug',
  async (slug, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = state?.user?.userInfo?.isAdmin ? state?.user?.userInfo?.token : '';
      const qs = token ? `?adminCheck=${encodeURIComponent(token)}` : '';
      const { data } = await axios.get(`/api/products/slug/${encodeURIComponent(slug)}${qs}`);
      localStorage.setItem('thisisinside', JSON.stringify(data));
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message
      );
    }
  }
);

export const listProducts = createAsyncThunk(
  'productList/listProducts',
  async (
    {
      keyword = '',
      sortBy = 'name',
      order = 'asc',
      adminCheck,
      pageNumber = 1,
      pageSize,
      random = false,
      exclude = [],
      append = false,
    },
    { rejectWithValue, signal }
  ) => {
    try {
      const excludeParam = Array.isArray(exclude) ? exclude.filter(Boolean).join(',') : (exclude || '')
      const pageSizeParam = (pageSize === undefined || pageSize === null) ? '' : String(pageSize)
      const randomParam = random ? '1' : ''
      const { data } = await axios.get(
        `/api/products?keyword=${encodeURIComponent(keyword)}&sortBy=${encodeURIComponent(sortBy)}&order=${encodeURIComponent(order)}&adminCheck=${encodeURIComponent(adminCheck ?? '')}&pageNumber=${encodeURIComponent(pageNumber)}&pageSize=${encodeURIComponent(pageSizeParam)}&random=${encodeURIComponent(randomParam)}&exclude=${encodeURIComponent(excludeParam)}`,
        { signal }
      );
      // Return payload and append flag so reducer knows how to merge
      return { ...data, __append: append };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);



export const deleteProduct = createAsyncThunk(
  'productList/deleteProduct',
  async (id, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state);
      await axios.delete(`/api/products/${id}`, config);
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      if (message === 'Not authorized, token failed') {
        dispatch(logout());
      }
      return rejectWithValue(message);
    }
  }
);

export const createProduct = createAsyncThunk(
  'productList/createProduct',
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state);
      const { data } = await axios.post('/api/products', {}, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      if (message === 'Not authorized, token failed') {
        dispatch(logout());
      }
      return rejectWithValue(message);
    }
  }
);

export const uploadProductImage = createAsyncThunk(
  'productList/uploadProductImage',
  async (product, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
      const { data } = await axios.put(`/api/products/image/${product._id}`, product, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      if (message === 'Not authorized, token failed') {
        dispatch(logout());
      }
      return rejectWithValue(message);
    }
  }
);

export const updateProduct = createAsyncThunk(
  'productList/updateProduct',
  async (product, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
      const { data } = await axios.put(`/api/products/${product._id}`, product, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      if (message === 'Not authorized, token failed') {
        dispatch(logout());
      }
      return rejectWithValue(message);
    }
  }
);

// Bulk enable/disable products
export const bulkUpdateProductStatus = createAsyncThunk(
  'productList/bulkUpdateProductStatus',
  async ({ ids, disabled }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
      const { data } = await axios.put('/api/products/bulk-status', { ids, disabled }, config);
      return data; // { matchedCount, modifiedCount }
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      if (message === 'Not authorized, token failed') {
        dispatch(logout());
      }
      return rejectWithValue(message);
    }
  }
);

// Delete one or more images from a product's upload folder, then persist pruned imageB/image/variants
export const deleteImages = createAsyncThunk(
  'productList/deleteImages',
  async ({ productId, folder, srcs, nextImage, nextImageB, nextVariants }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
      const { data: result } = await axios.delete(`/api/upload/${encodeURIComponent(folder)}`, { ...config, data: { srcs } });

      // Proceed with updating the product regardless; frontend computed next state
      const payload = { _id: productId };
      if (Array.isArray(nextImageB)) payload.imageB = nextImageB;
      if (typeof nextImage === 'string') payload.image = nextImage;
      if (Array.isArray(nextVariants)) payload.colorsVariants = nextVariants;
      await dispatch(updateProduct(payload)).unwrap();
      return result; // { deleted, failed }
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      return rejectWithValue(message);
    }
  }
);

export const createProductReview = createAsyncThunk(
  'productList/createProductReview',
  async ({ productId, review }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
      await axios.post(`/api/products/${productId}/reviews`, review, config);
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      if (message === 'Not authorized, token failed') {
        dispatch(logout());
      }
      return rejectWithValue(message);
    }
  }
);

export const adminCreateProductReview = createAsyncThunk(
  'productList/adminCreateProductReview',
  async ({ productId, review }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
      const { data } = await axios.post(`/api/products/${productId}/reviews/admin`, review, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      if (message === 'Not authorized, token failed') {
        dispatch(logout());
      }
      return rejectWithValue(message);
    }
  }
);

export const adminUpdateProductReview = createAsyncThunk(
  'productList/adminUpdateProductReview',
  async ({ productId, reviewId, review }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
      const { data } = await axios.put(`/api/products/${productId}/reviews/${reviewId}`, review, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      if (message === 'Not authorized, token failed') {
        dispatch(logout());
      }
      return rejectWithValue(message);
    }
  }
);

export const adminDeleteProductReview = createAsyncThunk(
  'productList/adminDeleteProductReview',
  async ({ productId, reviewId }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state);
      const { data } = await axios.delete(`/api/products/${productId}/reviews/${reviewId}`, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      if (message === 'Not authorized, token failed') {
        dispatch(logout());
      }
      return rejectWithValue(message);
    }
  }
);

export const adminBulkDeleteProductReviews = createAsyncThunk(
  'productList/adminBulkDeleteProductReviews',
  async ({ productId, reviewIds }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
      const { data } = await axios.post(`/api/products/${productId}/reviews/bulk-delete`, { reviewIds }, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      if (message === 'Not authorized, token failed') {
        dispatch(logout());
      }
      return rejectWithValue(message);
    }
  }
);

export const importProductReviews = createAsyncThunk(
  'productList/importProductReviews',
  async ({ productId, reviews }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { headers: { 'Content-Type': 'application/json' } });
      const { data } = await axios.post(`/api/products/${productId}/reviews/import`, { reviews }, config);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      if (message === 'Not authorized, token failed') {
        dispatch(logout());
      }
      return rejectWithValue(message);
    }
  }
);

export const listTopProducts = createAsyncThunk(
  'productList/listTopProducts',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/products/top');
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// --- Slice ---

const productListSlice = createSlice({
  name: 'productList',
  initialState: {
     productDetails: {},
  loadingDetails: false,
  errorDetails: null,
    products: [],
    product: {},
    topProducts: [],
    loadingTop: false,
    errorTop: null,
    page: 1,
    pages: 1,
    loading: false,
    error: null,
    // Tracks the most recently dispatched listProducts request so a slower,
    // superseded request (e.g. the home feed's random batch, still in
    // flight when a search starts) can't land after and overwrite it.
    currentRequestId: null,
    loadingDelete: false,
    successDelete: false,
    loadingCreate: false,
    successCreate: false,
    loadingUpdate: false,
    successUpdate: false,
    loadingImageUpload: false,
    successImageUpload: false,
    errorImageUpload: null,
    successReview: false,
    loadingReview: false,
    loadingAdminReviewOp: false,
    successAdminReviewOp: false,
    errorAdminReviewOp: null,
    loadingImportReviews: false,
    successImportReviews: false,
    errorImportReviews: null,
    loadingBulkStatus: false,
    successBulkStatus: false,
    errorBulkStatus: null,
    lastBulkResult: null,
  },
  reducers: {
    resetUpdateProduct(state) {
      state.loadingUpdate = false;
      state.successUpdate = false;
      state.error = null;
    },
    resetCreateProduct(state) {
      state.loadingCreate = false;
      state.successCreate = false;
      state.error = null;
      state.product = {};
    },
    resetCreateReview(state) {
      state.successReview = false;
      state.error = null;
    },
    resetAdminReviewOp(state) {
      state.loadingAdminReviewOp = false;
      state.successAdminReviewOp = false;
      state.errorAdminReviewOp = null;
    },
    resetImageUpload(state) {
      state.loadingImageUpload = false;
      state.successImageUpload = false;
      state.errorImageUpload = null;
    },
    resetImportReviews(state) {
      state.loadingImportReviews = false;
      state.successImportReviews = false;
      state.errorImportReviews = null;
    },
  },
  extraReducers: (builder) => {
    builder
    

    .addCase(fetchProductDetails.pending, (state) => {
  state.loadingDetails = true;
  state.errorDetails = null;
})
.addCase(fetchProductDetails.fulfilled, (state, action) => {
  state.loadingDetails = false;
  state.productDetails = action.payload;
})
.addCase(fetchProductDetails.rejected, (state, action) => {
  state.loadingDetails = false;
  state.errorDetails = action.payload;
})
    
      // fetchProducts
      .addCase(listProducts.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(listProducts.fulfilled, (state, action) => {
        const append = !!action.payload.__append;
        const isStale = action.meta.requestId !== state.currentRequestId;
        // A stale *replace* landing late (e.g. the home feed's random batch
        // resolving after a search request) is a no-op, not an overwrite --
        // this is the actual fix for the search-results race. Stale
        // *appends* still apply: infinite-scroll batches are additive and
        // can legitimately resolve out of order without being wrong.
        if (isStale && !append) return;
        state.loading = false;
        const incoming = action.payload.products || [];
        if (append) {
          // Append as-is. Duplicates across fetches are allowed (UI can show repeats),
          // but each fetch batch should already be unique server-side.
          state.products = [...(state.products || []), ...incoming];
        } else {
          state.products = incoming;
        }
        state.page = action.payload.page;
        state.pages = action.payload.pages;
      })
      .addCase(listProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchProductDetails
      .addCase(listProductDetails.pending, (state) => {
        state.loadingDetails = true;
        state.errorDetails = null;
      })
      .addCase(listProductDetails.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.productDetails = action.payload;
      })
      .addCase(listProductDetails.rejected, (state, action) => {
        state.loadingDetails = false;
        state.errorDetails = action.payload;
      })
      // fetchProductDetailsBySlug
      .addCase(listProductDetailsBySlug.pending, (state) => {
        state.loadingDetails = true;
        state.errorDetails = null;
      })
      .addCase(listProductDetailsBySlug.fulfilled, (state, action) => {
        state.loadingDetails = false;
        state.productDetails = action.payload;
      })
      .addCase(listProductDetailsBySlug.rejected, (state, action) => {
        state.loadingDetails = false;
        state.errorDetails = action.payload;
      })
      // deleteProduct
      .addCase(deleteProduct.pending, (state) => {
        state.loadingDelete = true;
        state.successDelete = false;
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state) => {
        state.loadingDelete = false;
        state.successDelete = true;
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.loadingDelete = false;
        state.error = action.payload;
      })
      // createProduct
      .addCase(createProduct.pending, (state) => {
        state.loadingCreate = true;
        state.successCreate = false;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.loadingCreate = false;
        state.successCreate = true;
        state.product = action.payload;
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loadingCreate = false;
        state.error = action.payload;
      })
      // uploadProductImage
      .addCase(uploadProductImage.pending, (state) => {
        state.loadingImageUpload = true;
        state.successImageUpload = false;
        state.errorImageUpload = null;
      })
      .addCase(uploadProductImage.fulfilled, (state, action) => {
        state.loadingImageUpload = false;
        state.successImageUpload = true;
        state.errorImageUpload = null;
        state.product = action.payload;
      })
      .addCase(uploadProductImage.rejected, (state, action) => {
        state.loadingImageUpload = false;
        state.successImageUpload = false;
        state.errorImageUpload = action.payload || 'Image upload failed';
      })
      // updateProduct
      .addCase(updateProduct.pending, (state) => {
        state.loadingUpdate = true;
        state.successUpdate = false;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.loadingUpdate = false;
        state.successUpdate = true;
        state.product = action.payload;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload;
      })
      // deleteImages
      .addCase(deleteImages.pending, (state) => {
        // no global spinner required; screen manages deleteBusy
      })
      .addCase(deleteImages.fulfilled, (state, action) => {
        // Optionally, could reconcile product from server, but updateProduct already did
      })
      .addCase(deleteImages.rejected, (state, action) => {
        // Surface error if needed
        state.error = action.payload;
      })
      // bulkUpdateProductStatus
      .addCase(bulkUpdateProductStatus.pending, (state) => {
        state.loadingBulkStatus = true;
        state.successBulkStatus = false;
        state.errorBulkStatus = null;
      })
      .addCase(bulkUpdateProductStatus.fulfilled, (state, action) => {
        state.loadingBulkStatus = false;
        state.successBulkStatus = true;
        state.lastBulkResult = action.payload;
      })
      .addCase(bulkUpdateProductStatus.rejected, (state, action) => {
        state.loadingBulkStatus = false;
        state.errorBulkStatus = action.payload;
      })
      // createProductReview
      .addCase(createProductReview.pending, (state) => {
        state.loadingReview = true;
        state.successReview = false;
        state.error = null;
      })
      .addCase(createProductReview.fulfilled, (state) => {
        state.loadingReview = false;
        state.successReview = true;
      })
      .addCase(createProductReview.rejected, (state, action) => {
        state.loadingReview = false;
        state.error = action.payload;
      })

      // adminCreateProductReview
      .addCase(adminCreateProductReview.pending, (state) => {
        state.loadingAdminReviewOp = true;
        state.successAdminReviewOp = false;
        state.errorAdminReviewOp = null;
      })
      .addCase(adminCreateProductReview.fulfilled, (state) => {
        state.loadingAdminReviewOp = false;
        state.successAdminReviewOp = true;
      })
      .addCase(adminCreateProductReview.rejected, (state, action) => {
        state.loadingAdminReviewOp = false;
        state.errorAdminReviewOp = action.payload;
      })

      // adminUpdateProductReview
      .addCase(adminUpdateProductReview.pending, (state) => {
        state.loadingAdminReviewOp = true;
        state.successAdminReviewOp = false;
        state.errorAdminReviewOp = null;
      })
      .addCase(adminUpdateProductReview.fulfilled, (state) => {
        state.loadingAdminReviewOp = false;
        state.successAdminReviewOp = true;
      })
      .addCase(adminUpdateProductReview.rejected, (state, action) => {
        state.loadingAdminReviewOp = false;
        state.errorAdminReviewOp = action.payload;
      })

      // adminDeleteProductReview
      .addCase(adminDeleteProductReview.pending, (state) => {
        state.loadingAdminReviewOp = true;
        state.successAdminReviewOp = false;
        state.errorAdminReviewOp = null;
      })
      .addCase(adminDeleteProductReview.fulfilled, (state) => {
        state.loadingAdminReviewOp = false;
        state.successAdminReviewOp = true;
      })
      .addCase(adminDeleteProductReview.rejected, (state, action) => {
        state.loadingAdminReviewOp = false;
        state.errorAdminReviewOp = action.payload;
      })

      // adminBulkDeleteProductReviews
      .addCase(adminBulkDeleteProductReviews.pending, (state) => {
        state.loadingAdminReviewOp = true;
        state.successAdminReviewOp = false;
        state.errorAdminReviewOp = null;
      })
      .addCase(adminBulkDeleteProductReviews.fulfilled, (state) => {
        state.loadingAdminReviewOp = false;
        state.successAdminReviewOp = true;
      })
      .addCase(adminBulkDeleteProductReviews.rejected, (state, action) => {
        state.loadingAdminReviewOp = false;
        state.errorAdminReviewOp = action.payload;
      })

      // importProductReviews
      .addCase(importProductReviews.pending, (state) => {
        state.loadingImportReviews = true;
        state.successImportReviews = false;
        state.errorImportReviews = null;
      })
      .addCase(importProductReviews.fulfilled, (state, action) => {
        state.loadingImportReviews = false;
        state.successImportReviews = action.payload; // Store the response data, not just true
      })
      .addCase(importProductReviews.rejected, (state, action) => {
        state.loadingImportReviews = false;
        state.errorImportReviews = action.payload;
      })

      // fetchTopProducts
      .addCase(listTopProducts.pending, (state) => {
        state.loadingTop = true;
        state.errorTop = null;
      })
      .addCase(listTopProducts.fulfilled, (state, action) => {
        state.loadingTop = false;
        state.topProducts = action.payload;
      })
      .addCase(listTopProducts.rejected, (state, action) => {
        state.loadingTop = false;
        state.errorTop = action.payload;
      });
  },
});



export const { resetCreateReview, resetAdminReviewOp, resetUpdateProduct, resetCreateProduct, resetImageUpload, resetImportReviews } = productListSlice.actions;



export default productListSlice.reducer;








